import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCustomerOnlinePayments from '../../hooks/useCustomerOnlinePayments';
import useConsentGate from '../../hooks/useConsentGate';
import { ConsentContextType } from '../../services/consentFormService';
import {
  Alert,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import { CardMembership as MembershipIcon } from '@mui/icons-material';
import CustomerMembershipPlanCard from '../../components/customer-memberships/CustomerMembershipPlanCard';
import CustomerPageHeader from '../../components/customer/CustomerPageHeader';
import CustomerPageLoading from '../../components/customer/CustomerPageLoading';
import CustomerPageSection from '../../components/customer/CustomerPageSection';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StripeEmbeddedCheckoutDialog from '../../components/common/StripeEmbeddedCheckoutDialog';
import {
  allowsInStore,
  allowsOnline,
  normalizeCustomerPaymentMode,
} from '../../constants/CustomerPaymentMode';
import {
  cancelCustomerMembership,
  createMembershipCheckoutSession,
  getCustomerMemberships,
  getStoreMembershipPlans,
  pauseCustomerMembership,
  resumeCustomerMembershipCancellation,
  resumeCustomerMembershipFromPause,
} from '../../services/customerMembershipService';
import { useStore } from '../../context/StoreContext';
import { CUSTOMER_COLORS } from '../../constants/customerTheme';
import { buildCustomerRoute } from '../../utils/storeUtils';
import { formatUtcDate } from '../../utils/dateFormatUtils';
import { resolveMembershipPaidThroughUtc } from '../../utils/membershipBookingUtils';

const ACTIVE_MEMBERSHIP_STATUSES = new Set(['Active', 'Trialing', 'PastDue', 'Paused']);
const PENDING_CHECKOUT_STATUS = 'PendingCheckout';
const PAUSABLE_STATUSES = new Set(['Active', 'Trialing']);

const isRelevantMembership = (membership) => (
  ACTIVE_MEMBERSHIP_STATUSES.has(membership?.status) || membership?.status === PENDING_CHECKOUT_STATUS
);

const updateMembershipForPlan = (memberships, planId, updatedMembership) => {
  const otherMemberships = memberships.filter((membership) => membership.membershipPlanId !== planId);
  return [...otherMemberships, updatedMembership];
};

const StoreMembershipsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { storeId, storeOverview } = useStore();
  const { ensureConsents, consentDialog } = useConsentGate(storeId);

  const [plans, setPlans] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionPlanId, setActionPlanId] = useState(null);
  const [checkoutFeedback, setCheckoutFeedback] = useState(null);
  const [cancelPlanId, setCancelPlanId] = useState(null);
  const [pausePlanId, setPausePlanId] = useState(null);
  const [checkoutPlanId, setCheckoutPlanId] = useState(null);

  const currency = storeOverview?.currency || plans[0]?.currency || 'EUR';
  const membershipPaymentMode = normalizeCustomerPaymentMode(storeOverview?.membershipCustomerPaymentMode);
  const membershipAllowsInStore = allowsInStore(membershipPaymentMode);
  // A store can take a given customer's payments in store only, whatever it offers everyone else.
  const customerOnlinePaymentsAllowed = useCustomerOnlinePayments(storeId);
  const membershipAllowsOnline = customerOnlinePaymentsAllowed && allowsOnline(membershipPaymentMode);

  const membershipsByPlanId = useMemo(() => (
    memberships.reduce((accumulator, membership) => {
      if (membership?.membershipPlanId) {
        accumulator[membership.membershipPlanId] = membership;
      }
      return accumulator;
    }, {})
  ), [memberships]);

  useEffect(() => {
    if (storeOverview && !storeOverview.hasActiveMembershipPlans) {
      navigate(buildCustomerRoute(storeId, ''), { replace: true });
    }
  }, [storeOverview, storeId, navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [plansResult, membershipsResult] = await Promise.all([
        getStoreMembershipPlans(storeId),
        getCustomerMemberships(storeId),
      ]);

      setPlans(plansResult);
      setMemberships(membershipsResult);
    } catch (err) {
      console.error('Failed to load memberships:', err);
      setError(err?.response?.data?.message || t('customer.memberships.error.loading'));
    } finally {
      setLoading(false);
    }
  }, [storeId, t]);

  useEffect(() => {
    if (storeOverview && !storeOverview.hasActiveMembershipPlans) {
      return;
    }
    loadData();
  }, [loadData, storeOverview]);

  useEffect(() => {
    // Present when a customer returns from an off-site payment step (e.g. Klarna or
    // Scalapay approval) via the checkout session's ReturnUrl. Stripe returns them here
    // for failures too, so this cannot claim success — the reloaded plans below show the
    // real subscription state once the payment webhook lands.
    if (!searchParams.get('session_id')) {
      return;
    }

    setCheckoutFeedback({ severity: 'info', message: t('customer.memberships.checkout.processing') });
    loadData();

    searchParams.delete('session_id');
    setSearchParams(searchParams, { replace: true });
  }, [loadData, searchParams, setSearchParams, t]);

  const sortedPlans = useMemo(() => (
    [...plans].sort((planA, planB) => {
      const aIsSubscribed = isRelevantMembership(membershipsByPlanId[planA.id]);
      const bIsSubscribed = isRelevantMembership(membershipsByPlanId[planB.id]);

      if (aIsSubscribed && !bIsSubscribed) return -1;
      if (!aIsSubscribed && bIsSubscribed) return 1;
      return 0;
    })
  ), [plans, membershipsByPlanId]);

  const runMembershipAction = async (planId, action, successMessage) => {
    setActionPlanId(planId);
    setCheckoutFeedback(null);

    try {
      const updatedMembership = await action();
      setMemberships((currentMemberships) => updateMembershipForPlan(currentMemberships, planId, updatedMembership));
      setCheckoutFeedback({ severity: 'success', message: successMessage });
    } catch (err) {
      console.error('Membership action failed:', err);
      setCheckoutFeedback({
        severity: 'error',
        message: err?.response?.data?.message || err?.message || t('customer.memberships.error.loading'),
      });
    } finally {
      setActionPlanId(null);
    }
  };

  const handleBookPlan = (planId) => {
    navigate(buildCustomerRoute(storeId, 'booking'), {
      state: { preSelectedMembershipPlanId: planId },
    });
  };

  const handleSubscribe = async (planId) => {
    setCheckoutFeedback(null);
    try {
      const consent = await ensureConsents({
        membershipPlanId: planId,
        contextType: ConsentContextType.MembershipPurchase,
        contextItemId: planId,
      });
      if (consent.accepted) {
        setCheckoutPlanId(planId);
      }
    } catch (consentError) {
      console.error('Failed to check consent forms:', consentError);
      setCheckoutFeedback({ severity: 'error', message: t('consentForms.errors.load') });
    }
  };

  const handleCheckoutComplete = () => {
    setCheckoutPlanId(null);
    setCheckoutFeedback({ severity: 'success', message: t('customer.memberships.checkout.success') });
    loadData();
  };

  const handleCancelConfirm = async () => {
    if (!cancelPlanId) {
      return;
    }

    const planIdToCancel = cancelPlanId;
    const membershipToCancel = membershipsByPlanId[planIdToCancel];
    const cancelDate = formatUtcDate(
      resolveMembershipPaidThroughUtc(membershipToCancel) || membershipToCancel?.currentPeriodEndUtc,
    );
    setCancelPlanId(null);

    await runMembershipAction(
      planIdToCancel,
      () => cancelCustomerMembership(storeId, planIdToCancel),
      t('customer.memberships.cancel.success', { date: cancelDate }),
    );
  };

  const handlePauseConfirm = async () => {
    if (!pausePlanId) {
      return;
    }

    const planIdToPause = pausePlanId;
    setPausePlanId(null);

    await runMembershipAction(
      planIdToPause,
      () => pauseCustomerMembership(storeId, planIdToPause),
      t('customer.memberships.pause.success')
    );
  };

  const handleResume = async (planId) => {
    await runMembershipAction(
      planId,
      () => resumeCustomerMembershipFromPause(storeId, planId),
      t('customer.memberships.resume.success')
    );
  };

  const handleResumeCancellation = async (planId) => {
    await runMembershipAction(
      planId,
      () => resumeCustomerMembershipCancellation(storeId, planId),
      t('customer.memberships.resumeCancellation.success')
    );
  };

  const getPlanCardProps = (plan) => {
    const membership = membershipsByPlanId[plan.id];
    const membershipStatus = membership?.status ?? null;
    const isActiveSubscription = membershipStatus && ACTIVE_MEMBERSHIP_STATUSES.has(membershipStatus);
    const isPendingCheckout = membershipStatus === PENDING_CHECKOUT_STATUS;
    const cancelAtPeriodEnd = Boolean(membership?.cancelAtPeriodEnd);
    const isPaused = Boolean(membership?.isPaused) || membershipStatus === 'Paused';
    const isPausableStatus = membershipStatus && PAUSABLE_STATUSES.has(membershipStatus);

    return {
      membershipStatus: isPaused ? 'Paused' : membershipStatus,
      entitlementUsage: membership?.entitlementUsage ?? null,
      isSubscribed: isActiveSubscription || isPendingCheckout,
      cancelAtPeriodEnd,
      cancelsAtUtc: membership?.cancelsAtUtc
        || resolveMembershipPaidThroughUtc(membership)
        || membership?.currentPeriodEndUtc
        || null,
      isPaused,
      canCancel: isPausableStatus
        && plan.allowCustomerCancellation
        && !cancelAtPeriodEnd
        && !isPaused,
      canPause: false,
      canResume: isActiveSubscription && isPaused,
      canResumeCancellation: isActiveSubscription && cancelAtPeriodEnd && !isPaused,
      // A checkout that was left or failed can be picked up again; the server reuses it.
      canSubscribe: !isActiveSubscription && membershipAllowsOnline,
      canBook: true,
      showInStoreOnlyMessage: !isActiveSubscription && !isPendingCheckout && !membershipAllowsOnline && membershipAllowsInStore,
      showBothModeHint: !isActiveSubscription && !isPendingCheckout && membershipAllowsOnline && membershipAllowsInStore,
      actionLoading: actionPlanId === plan.id,
    };
  };

  if (storeOverview && !storeOverview.hasActiveMembershipPlans) {
    return null;
  }

  return (
    <Box>
      <CustomerPageHeader
        icon={MembershipIcon}
        title={t('customer.memberships.title')}
        subtitle={t('customer.memberships.subtitle')}
      />

      {!membershipAllowsOnline ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('customer.memberships.browseOnly')}
        </Alert>
      ) : null}

      {checkoutFeedback ? (
        <Alert severity={checkoutFeedback.severity} sx={{ mb: 2 }} onClose={() => setCheckoutFeedback(null)}>
          {checkoutFeedback.message}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <CustomerPageLoading />
      ) : sortedPlans.length === 0 ? (
        <CustomerPageSection variant="empty" sx={{ textAlign: 'center' }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: CUSTOMER_COLORS.text, mb: 0.5 }}>
            {t('customer.memberships.empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('customer.memberships.empty.subtitle')}
          </Typography>
        </CustomerPageSection>
      ) : (
        <Grid container spacing={2}>
          {sortedPlans.map((plan) => {
            const cardProps = getPlanCardProps(plan);

            return (
              <Grid item xs={12} sm={6} md={4} key={plan.id}>
                <CustomerMembershipPlanCard
                  plan={plan}
                  currency={currency}
                  {...cardProps}
                  onSubscribe={handleSubscribe}
                  onBook={handleBookPlan}
                  onCancel={() => setCancelPlanId(plan.id)}
                  onPause={() => setPausePlanId(plan.id)}
                  onResume={() => handleResume(plan.id)}
                  onResumeCancellation={() => handleResumeCancellation(plan.id)}
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      <ConfirmDialog
        open={Boolean(cancelPlanId)}
        onClose={() => setCancelPlanId(null)}
        onConfirm={handleCancelConfirm}
        title={t('customer.memberships.cancel.confirmTitle')}
        message={t('customer.memberships.cancel.confirmMessage', {
          date: formatUtcDate(
            resolveMembershipPaidThroughUtc(membershipsByPlanId[cancelPlanId])
            || membershipsByPlanId[cancelPlanId]?.currentPeriodEndUtc,
          ),
        })}
        confirmText={t('customer.memberships.actions.cancel')}
        cancelText={t('common.cancel')}
        confirmColor="error"
      />

      <ConfirmDialog
        open={Boolean(pausePlanId)}
        onClose={() => setPausePlanId(null)}
        onConfirm={handlePauseConfirm}
        title={t('customer.memberships.pause.confirmTitle')}
        message={t('customer.memberships.pause.confirmMessage')}
        confirmText={t('customer.memberships.actions.pause')}
        cancelText={t('common.cancel')}
        confirmColor="primary"
      />

      {consentDialog}

      <StripeEmbeddedCheckoutDialog
        open={Boolean(checkoutPlanId)}
        onClose={() => setCheckoutPlanId(null)}
        createSession={() => createMembershipCheckoutSession(storeId, checkoutPlanId)}
        onComplete={handleCheckoutComplete}
      />
    </Box>
  );
};

export default StoreMembershipsPage;
